<script setup lang="ts">
import type { IDepositRate } from '~/components/entity/deposit/item/types'
import { useBreakpoints } from '@vueuse/core'
import { useCurrencyFormat } from '~/composables/useCurrencyFormat'

const KGS_CURRENCY_ID = 1
const COLUMN_MOBILE_BASE_WIDTH_PX = 110
const COLUMN_DESKTOP_BASE_HEIGHT_PX = 100
const COLUMN_SIZE_STEP_PX = 24
const COLUMN_ANIMATION_DELAY_MS = 50

const props = defineProps<{
  grouped: Record<string, IDepositRate[]>
  amount: number
  currencyCode: string
}>()

const breakpoints = useBreakpoints({
  mobile: 0,
  tablet: 640,
  desktop: 1024
})
const isMobile = breakpoints.smaller('tablet')

const getRateForCalculation = (rate: string, effectiveRate: string) => {
  const effectiveRateValue = Number(effectiveRate)
  if (effectiveRateValue > 0) return effectiveRateValue
  return Number(rate)
}

const calculateProfit = (rate: string, effectiveRate: string, period: number, amount: number) => {
  const rateValue = getRateForCalculation(rate, effectiveRate)
  if (isNaN(rateValue) || !amount) return 0
  return (amount * rateValue * period) / (100 * 12)
}

const calculateTotal = (rate: string, effectiveRate: string, period: number, amount: number) => {
  return amount + calculateProfit(rate, effectiveRate, period, amount)
}

const currencyId = computed(() => {
  const currencyList = useCurrencyStore().list
  const currency = currencyList.find((c) => c.code === props.currencyCode)
  return currency?.id || KGS_CURRENCY_ID
})

const currentRates = computed(() => props.grouped[props.currencyCode] ?? [])

const decoratedRates = computed(() =>
  currentRates.value.map((rate, index) => ({
    rate,
    hasEffectiveRate: Number(rate.effective_rate) > 0,
    total: calculateTotal(rate.rate, rate.effective_rate, rate.period, props.amount),
    columnStyle: isMobile.value
      ? {
          width: `${COLUMN_MOBILE_BASE_WIDTH_PX + index * COLUMN_SIZE_STEP_PX}px`,
          maxWidth: '100%',
          flex: 'none',
          animationDelay: `${index * COLUMN_ANIMATION_DELAY_MS}ms`
        }
      : {
          height: `${COLUMN_DESKTOP_BASE_HEIGHT_PX + index * COLUMN_SIZE_STEP_PX}px`,
          maxWidth: '200px',
          flex: '1',
          animationDelay: `${index * COLUMN_ANIMATION_DELAY_MS}ms`
        }
  }))
)
</script>

<template>
  <div class="space-y-4">
    <div v-if="!Object.keys(props.grouped).length" class="text-gray-500">
      {{ $t('common.no_available_interest_rates') }}
    </div>

    <template v-else>
      <Transition name="columns" mode="out-in">
        <div
          v-if="currentRates.length"
          :key="props.currencyCode"
          class="flex w-full flex-col gap-2 text-sm sm:flex-row sm:items-end"
        >
          <div
            v-for="item in decoratedRates"
            :key="item.rate.id"
            class="column-item flex flex-col items-center justify-between rounded-xl bg-gray-100 p-3 transition-colors hover:bg-blue-50"
            :style="item.columnStyle"
          >
            <div class="text-center font-semibold">
              {{ item.rate.rate }}%<span v-if="item.hasEffectiveRate">
                / {{ item.rate.effective_rate }}%*</span
              >
            </div>
            <div class="text-center text-sm text-gray-600">
              {{ item.rate.period }} {{ $t('common.months_short') }}
            </div>
            <div class="mt-1 text-center">
              {{ useCurrencyFormat(item.total, currencyId) }}
            </div>
          </div>
        </div>
      </Transition>
    </template>
  </div>
</template>

<style scoped>
.columns-enter-active,
.columns-leave-active {
  transition: all 0.3s ease;
}

.columns-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.columns-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.column-item {
  animation: slideUp 0.4s ease-out forwards;
  opacity: 0;
  transform: translateY(20px);
}

@keyframes slideUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: no-preference) {
  .column-item:hover {
    transform: scale(1.02);
    transition: transform 0.2s ease;
  }
}

@media (prefers-reduced-motion: reduce) {
  .columns-enter-active,
  .columns-leave-active,
  .column-item {
    animation: none;
    transition: none;
  }
}
</style>
